import type { TPermission } from '@/constants/permissions';

type TMatrixGroupKey = 'admissions' | 'members' | 'administration' | 'other';

type TMatrixZoneKey =
  | 'pipeline-lists'
  | 'applicant-decisions'
  | 'applicant-reassign'
  | 'whole-pipeline'
  | 'checks-review'
  | 'final-approval'
  | 'waitlist-activation'
  | 'member-invitations'
  | 'concierge-desk'
  | 'member-records'
  | 'member-reassign'
  | 'member-management'
  | 'billing-oversight'
  | 'admin-users'
  | 'concierge-oversight'
  | 'settings-policy'
  | 'safety-audit';

type TMatrixGroup = {
  key: TMatrixGroupKey;
  label: string;
};

type TMatrixZone = {
  key: TMatrixZoneKey;
  group: TMatrixGroupKey;
  label: string;
};

const MATRIX_GRANT_SEPARATOR = ' · ';

const MATRIX_GROUPS: readonly TMatrixGroup[] = [
  { key: 'admissions', label: 'Admissions' },
  { key: 'members', label: 'Members' },
  { key: 'administration', label: 'Administration' },
  { key: 'other', label: 'Other' },
];

const MATRIX_ZONES: readonly TMatrixZone[] = [
  { key: 'pipeline-lists', group: 'admissions', label: 'Pipeline lists & own recruits' },
  { key: 'applicant-decisions', group: 'admissions', label: 'Advance, reject, archive' },
  { key: 'applicant-reassign', group: 'admissions', label: 'Reassign applicant owner' },
  { key: 'whole-pipeline', group: 'admissions', label: 'Whole pipeline & all interviews' },
  { key: 'checks-review', group: 'admissions', label: 'Background check & further review' },
  { key: 'final-approval', group: 'admissions', label: 'Final approval & Boss applicants' },
  { key: 'waitlist-activation', group: 'admissions', label: 'Waitlist, capacity, activation links' },
  { key: 'member-invitations', group: 'admissions', label: 'Member invitations' },
  { key: 'concierge-desk', group: 'members', label: 'Concierge desk — own book' },
  { key: 'member-records', group: 'members', label: 'Member records' },
  { key: 'member-reassign', group: 'members', label: 'Reassign & change member type' },
  { key: 'member-management', group: 'members', label: 'Member management' },
  { key: 'billing-oversight', group: 'members', label: 'Billing oversight' },
  { key: 'admin-users', group: 'administration', label: 'Staff users' },
  { key: 'concierge-oversight', group: 'administration', label: 'Concierge oversight' },
  { key: 'settings-policy', group: 'administration', label: 'Settings & policy' },
  { key: 'safety-audit', group: 'administration', label: 'Safety reports & audit log' },
];

const PERMISSION_ZONE: Record<TPermission, TMatrixZoneKey | null> = {
  'home:view': null,
  'pipeline:overview': 'whole-pipeline',
  'applicants:view-new': 'pipeline-lists',
  'applicants:view-own': 'pipeline-lists',
  'applicants:view-all': 'whole-pipeline',
  'applicants:view-archived': 'whole-pipeline',
  'applicants:advance': 'applicant-decisions',
  'applicants:open-seat': 'applicant-decisions',
  'applicants:reject': 'applicant-decisions',
  'applicants:archive': 'applicant-decisions',
  'applicants:reassign': 'applicant-reassign',
  'interviews:list-own': 'pipeline-lists',
  'interviews:list-all': 'whole-pipeline',
  'interviews:calendar-own': 'pipeline-lists',
  'interviews:calendar-all': 'whole-pipeline',
  'background-check:view': 'checks-review',
  'background-check:view-failed': 'checks-review',
  'further-review:view': 'checks-review',
  'final-approval:decide': 'final-approval',
  'boss-applicants:manage': 'final-approval',
  'verified:view': 'pipeline-lists',
  'approved:view': 'pipeline-lists',
  'waitlist:manage': 'waitlist-activation',
  'capacity:view': 'waitlist-activation',
  'activation:view': 'waitlist-activation',
  'on-the-app:view': 'pipeline-lists',
  'invitations:manage': 'member-invitations',
  'clients:view-own': 'concierge-desk',
  'clients:view-all': 'member-records',
  'singles:view-own': 'concierge-desk',
  'singles:view-all': 'member-records',
  'singles:search': 'concierge-desk',
  'recs:create': 'concierge-desk',
  'recs:view-own': 'concierge-desk',
  'recs:view-all': 'concierge-desk',
  'messages:view': 'concierge-desk',
  'billing:desk': 'concierge-desk',
  'billing:review': 'billing-oversight',
  'billing:view-all': 'billing-oversight',
  'members:view': 'member-records',
  'members:manage': 'member-management',
  'members:reassign': 'member-reassign',
  'members:change-type': 'member-reassign',
  'admin:concierges': 'concierge-oversight',
  'admin:manage': 'admin-users',
  'admin:notifications': 'settings-policy',
  'admin:invitation-policy': 'settings-policy',
  'admin:safety-reports': 'safety-audit',
  'admin:audit-log': 'safety-audit',
};

const SERVER_ACTION_ZONE: Readonly<Record<string, TMatrixZoneKey>> = {
  'app_parameter_set': 'settings-policy',
  'seat_open': 'waitlist-activation',
  'staff_availability_set': 'admin-users',
  'staff_calendar_set': 'admin-users',
  'staff_factor_reset': 'admin-users',
  'staff_factor_view': 'admin-users',
  'staff_invite': 'admin-users',
  'staff_role_promote': 'admin-users',
  'thread_monitor': 'concierge-oversight',
};

export type {
  TMatrixGroup,
  TMatrixGroupKey,
  TMatrixZone,
  TMatrixZoneKey,
};
export {
  MATRIX_GRANT_SEPARATOR,
  MATRIX_GROUPS,
  MATRIX_ZONES,
  PERMISSION_ZONE,
  SERVER_ACTION_ZONE,
};
